// The Configure tab's one form: what it holds, where it starts, and the body
// it saves.
//
// Split out from the sections and the form itself the way the template editor
// splits `template-schema.ts` from its editor — the schema is the part worth
// reading on its own, and the part worth testing without a DOM.
//
// It is one schema because it is one save. General, Filters and Tuning are all
// columns on the screens row, and PATCH /v1/screens/{id} takes all of them
// (see crates/ewt-server/src/screens.rs). `offsetMinutes` is the exception and
// is absent on purpose: the offset is nudged from the Operate tab against its
// own endpoint, so carrying it here would let a Configure save revert a nudge
// made since the form was opened.

import { z } from "zod";
import type { Filter, PatchScreen, Screen } from "@/api/types";

/** Sentinel Select value standing in for "no template" (templateId: null). */
export const NO_TEMPLATE = "none";

/**
 * A numeric knob. The union admits the transient text states ("" and a lone
 * "-") so typing stays fluid; both fail validation at submit time, naming the
 * field. Ranges mirror `tuning::validate_ranges` exactly — the server is the
 * authority.
 */
function range(label: string, min: number, max: number) {
    return z
        .union([z.number(), z.literal(""), z.literal("-")])
        .refine((v) => typeof v === "number", { message: `${label} is required` })
        .refine((v) => typeof v !== "number" || (v >= min && v <= max), {
            message: `Must be between ${min} and ${max}`,
        });
}

export const filterRowSchema = z.object({
    property: z.enum(["orderDeviceId", "transactionOperatorName"]),
    mode: z.enum(["include", "exclude"]),
    /** One comma-separated text input per row, not a chip picker (matches the brief). */
    valuesText: z.string(),
});

export const configureSchema = z.object({
    name: z.string().trim().min(1, "Name is required"),
    active: z.boolean(),
    templateId: z.string(),
    filters: z.array(filterRowSchema),
    count: range("Order count", 1, 100),
    cutoffMinutes: range("Cutoff", 1, 1440),
    minimumMinutes: range("Minimum", 0, 240),
    outlierCount: range("Outliers", 0, 99),
});

/** What the fields hold, including the transient numeric text states. */
export type ConfigureValues = z.input<typeof configureSchema>;
/** What submit receives, with every knob narrowed to a number. */
export type ConfigureOutput = z.output<typeof configureSchema>;

export const NEW_FILTER_ROW: ConfigureValues["filters"][number] = {
    property: "orderDeviceId",
    mode: "include",
    valuesText: "",
};

export function toDefaults(screen: Screen): ConfigureValues {
    return {
        name: screen.name,
        active: screen.active,
        templateId: screen.templateId ?? NO_TEMPLATE,
        filters: screen.filters.map((filter) => ({
            property: filter.property,
            mode: filter.mode,
            valuesText: filter.values.join(", "),
        })),
        count: screen.count,
        cutoffMinutes: screen.cutoffMinutes,
        minimumMinutes: screen.minimumMinutes,
        outlierCount: screen.outlierCount,
    };
}

export function toBody(values: ConfigureOutput): PatchScreen {
    const filters: Filter[] = values.filters.map((row) => ({
        property: row.property,
        mode: row.mode,
        values: row.valuesText
            .split(",")
            .map((value) => value.trim())
            .filter((value) => value.length > 0),
    }));
    return {
        name: values.name,
        active: values.active,
        // An explicit null, not an omitted key: PATCH tells the two apart, so
        // one request can both set and clear the template.
        templateId: values.templateId === NO_TEMPLATE ? null : values.templateId,
        filters,
        count: values.count,
        cutoffMinutes: values.cutoffMinutes,
        minimumMinutes: values.minimumMinutes,
        outlierCount: values.outlierCount,
    };
}
